"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useBasketProviderData = void 0;
var BasketUpdated = _interopRequireWildcard(require("@ori-events/basket-updated"));
var PlaceOrderRequested = _interopRequireWildcard(require("@ori-events/place-order-requested"));
var RequestClearShoppingCache = _interopRequireWildcard(require("@ori-events/request-clear-shopping-cache"));
var RequestShoppingContext = _interopRequireWildcard(require("@ori-events/request-shopping-context"));
var ShoppingContextLoaded = _interopRequireWildcard(require("@ori-events/shopping-context-loaded"));
var _auth = require("@ori/auth");
var _environment = require("@ori/environment");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _react = require("react");
var _constants = require("../constants");
var _shoppingContextActions = require("../constants/shoppingContextActions");
var _logger = require("../logger");
var _utils = require("../utils");
var _storage = require("../utils/storage");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/** @deprecated */
let isReadyPromiseResolve;

/**
 * Legacy promise indicating that shopping bag provider handlers are connected
 * and other components can start communicating with the provider via events.
 * @deprecated Please, use the isBasketProviderListening flag & polling instead.
 */
const isReady = new Promise(resolve => {
  isReadyPromiseResolve = resolve;
});

// Publish the promise to window scope for other components.
if (typeof window !== 'undefined') {
  window.isBasketProviderReady = isReady;
}

/** Creates event listeners for the actions defined in shoppingContextActions list. */
function getEventListeners(currentSettings, createEventListener, createRequestShoppingContextEventListener) {
  const result = [];
  for (const [triggerActionEventName, {
    handler
  }] of _shoppingContextActions.shoppingContextActions.entries()) {
    const eventListenerFactory = triggerActionEventName === RequestShoppingContext.Name ? createRequestShoppingContextEventListener : createEventListener;
    result.push({
      eventName: triggerActionEventName,
      eventListener: eventListenerFactory(handler, currentSettings)
    });
  }
  return result;
}
const useBasketProviderData = () => {
  const globalApiClient = (0, _globalApiClientReact.useApiClient)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    customerId
  } = (0, _auth.useToken)();

  // Indication that startup shopping context data initialization was done.
  const [isInitialShoppingContextApplied, setIsInitialShoppingContextApplied] = (0, _react.useState)(false);

  // Indication that shopping context was requested during startup.
  const wasShoppingContextRequestedDuringInit = (0, _react.useRef)(false);

  // Indication that startup loading of shopping context is already in progress.
  // Rather than triggering new requests, we wait for the 1st one to return data.
  // This is valid mostly for startup, as multiple apps can be asking for shopping
  // context via the request-shopping-context event in parallel. For later updates,
  // this is not a problem as the updates are triggered by user actions.
  const isRequestingShoppingContext = (0, _react.useRef)(false);

  // Current shopping context / shopping session data. This is also being synchronized
  // with the cache on startup & after each update action.
  const [shoppingContext, setShoppingContext] = (0, _react.useState)();
  const shoppingSessionId = (0, _react.useRef)('');

  /**
   * Sets current shopping context data state while optionally setting also the cache
   * and/or triggering a requested success response event.
   */
  const updateContext = (0, _react.useCallback)(async (data, eventTypeToBeTriggered, shouldStoreData = true) => {
    shoppingSessionId.current = data.sessionId ?? '';
    setShoppingContext(data);
    if (shouldStoreData && (0, _utils.isStorageAvailable)()) {
      await (0, _utils.setStorageItem)(_constants.CONTEXT_KEY, data);
      (0, _logger.logDebug)('Stored context was updated with new data:\n\t', data);
    } else {
      (0, _logger.logDebug)('Current context state was updated with new data:\n\t', data);
    }
    if (eventTypeToBeTriggered) {
      (0, _logger.logDebug)(`Triggering ${eventTypeToBeTriggered} event ...`);
      (0, _utils.dispatchBasketUpdatedEvent)(data, eventTypeToBeTriggered);
    }
  }, []);

  /** Tries to read last cache and, if found, sets it back into current shopping context state. */
  const getStoredContext = (0, _react.useCallback)(async () => {
    const data = await (0, _utils.getStorageItem)(_constants.CONTEXT_KEY);
    if (!data) {
      return;
    }
    await updateContext(data, undefined, false);
    (0, _logger.logDebug)('Restored context data from storage.');
  }, [updateContext]);

  /** Clears current shopping context data and stored cache. */
  const clearContext = (0, _react.useCallback)(async () => {
    (0, _logger.logDebug)('Clearing stored shopping context data ...');
    await (0, _utils.removeStorageItem)(_constants.CONTEXT_KEY);
    setShoppingContext(undefined);
  }, []);

  /** Clears shopping context data based on received event. */
  const clearContextOnEvent = (0, _react.useCallback)(async event => {
    (0, _utils.logReceivedEvent)(event);
    await clearContext();
    if (RequestClearShoppingCache.isRequestClearShoppingCacheEvent(event) && event.detail?.isImmediateReloadRequired) {
      (0, _logger.logDebug)('Immediate shopping context reload was requested.');
      setTimeout(() => dispatchEvent(new CustomEvent(RequestShoppingContext.Name)));
    }
  }, [clearContext]);

  /** Generic event listener for most update actions. */
  const createEventListener = (0, _react.useCallback)((handler, currentSettings) => {
    const result = async event => {
      (0, _utils.logReceivedEvent)(event);
      const data = await handler(event, currentSettings);
      if (data === false) {
        (0, _logger.logDebug)('Shopping context update failed.');
      } else {
        await updateContext(data, BasketUpdated.Name);
      }
    };
    return result;
  }, [updateContext]);

  /**
   * Specific event listener for request-shopping-context action.
   * Getting the shopping context data from storage and/or the isRequestingShoppingContext
   * state is involved in this case. If in cache, the data is returned w/o creating
   * another request to server. If the loading already started before, it waits for
   * the original request to return the data.
   */
  const createRequestShoppingContextEventListener = (0, _react.useCallback)((handler, currentSettings) => {
    const result = async event => {
      (0, _utils.logReceivedEvent)(event);
      if (shoppingContext) {
        const triggerResponse = () => {
          (0, _logger.logDebug)(`Context data was loaded before. Triggering ${ShoppingContextLoaded.Name} event ...`);
          (0, _utils.dispatchBasketUpdatedEvent)(shoppingContext, ShoppingContextLoaded.Name);
        };

        // postpone a bit to have same events order as when really fetching the data
        setTimeout(() => triggerResponse());
        return;
      }
      if (!isInitialShoppingContextApplied) {
        // In case the initial storage & shopping context check wasn't done yet,
        // just schedule for later with this flag.
        (0, _logger.logDebug)('Shopping context was requested during initialization.');
        wasShoppingContextRequestedDuringInit.current = true;
        return;
      }
      if (isRequestingShoppingContext.current) {
        (0, _logger.logDebug)('Shopping context request was already called before. Waiting for response ...');
        return;
      }
      isRequestingShoppingContext.current = true;
      (0, _logger.logDebug)('Loading shopping context ...');
      const data = await handler(event, currentSettings);
      if (data === false) {
        (0, _logger.logDebug)('Shopping context load failed.');
      } else {
        (0, _logger.logDebug)('Shopping context was loaded.');
        await updateContext(data, ShoppingContextLoaded.Name);
      }
      isRequestingShoppingContext.current = false;
    };
    return result;
  }, [isInitialShoppingContextApplied, isRequestingShoppingContext, shoppingContext, updateContext]);

  // init event listeners
  (0, _react.useEffect)(() => {
    const eventListeners = getEventListeners({
      client: globalApiClient,
      customerId: shoppingContext?.customerId ?? customerId,
      sessionId: shoppingSessionId.current,
      tenant
    }, createEventListener, createRequestShoppingContextEventListener);
    eventListeners.push(
    // This is now called when the user requests creating a new order.
    // That means that the cache should be cleared. But we don't know yet
    // if the create order action succeeds. Getting exact order creation
    // result / success might be problematic, as there are page redirects
    // involved, and after page reload, it is too late for clearing caches.
    // So current solution calls the PlaceOrderRequested event before
    // calling the request on server, so that there is enough time to
    // do this cache cleanup here before the redirects happen.
    // ATM, the redirect is being done in legacy module & this logic might
    // change after rewriting to React.
    {
      eventName: PlaceOrderRequested.Name,
      eventListener: event => void clearContextOnEvent(event)
    },
    // This is now called for other cases when clearing context data is needed.
    {
      eventName: RequestClearShoppingCache.Name,
      eventListener: event => void clearContextOnEvent(event)
    });
    eventListeners.forEach(({
      eventName,
      eventListener
    }) => {
      window.addEventListener(eventName, eventListener);
    });
    (0, _logger.logDebug)('Event listeners are connected.');

    // Set the ready flag to notify consuming components.
    window.isBasketProviderListening = true;
    isReadyPromiseResolve(true);
    return () => {
      eventListeners.forEach(({
        eventName,
        eventListener
      }) => {
        window.removeEventListener(eventName, eventListener);
      });
      eventListeners.length = 0;
      (0, _logger.logDebug)('Old event listeners were disconnected.');
    };
  }, [clearContextOnEvent, createEventListener, createRequestShoppingContextEventListener, shoppingContext?.customerId, customerId, globalApiClient, tenant]);

  // setup initial state of shopping context data
  const applyInitialShoppingContext = (0, _react.useCallback)(async () => {
    (0, _logger.logDebug)('Checking initial shopping context state ...');
    if ((0, _utils.isStorageAvailable)()) {
      if ((0, _utils.isPageMarkedForCacheCleanup)() || (0, _storage.isScheduledCacheCleanup)()) {
        (0, _storage.removeScheduledCacheCleanup)();
        await clearContext();
      } else {
        await getStoredContext();
      }
    }
    setIsInitialShoppingContextApplied(true);
    (0, _logger.logDebug)('Initial shopping context check was done.');
  }, [clearContext, getStoredContext]);

  // mangling with the initial state of cached data
  // & optional request-shopping-context events received during init
  (0, _react.useEffect)(() => {
    if (!isInitialShoppingContextApplied) {
      void applyInitialShoppingContext();
    } else if (wasShoppingContextRequestedDuringInit.current) {
      // In case there were some request-shopping-context events during init,
      // re-trigger an event here to go through proper handler.
      wasShoppingContextRequestedDuringInit.current = false;
      setTimeout(() => {
        (0, _logger.logDebug)('Re-triggering request-shopping-context event ...');
        dispatchEvent(new CustomEvent(RequestShoppingContext.Name));
      }, _constants.POST_INIT_EVENT_DELAY);
    }
  }, [applyInitialShoppingContext, isInitialShoppingContextApplied]);
  const contextValue = (0, _react.useMemo)(() => ({
    isRequestingShoppingContext,
    shoppingContext
  }), [isRequestingShoppingContext, shoppingContext]);
  return contextValue;
};
exports.useBasketProviderData = useBasketProviderData;
